import { useState } from "react";
import { formatDateOnly } from "@/lib/date-only";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { apiGet, apiPost, apiPatch } from "@/lib/api";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import {
  ChevronLeft,
  CheckCircle,
  Search,
  XCircle,
  Users,
  Loader2,
} from "lucide-react";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { useGoBack } from "@/hooks/use-go-back";

export const Route = createFileRoute("/_authenticated/incidents/$incidentId")({
  component: IncidentDetailPage,
});

const SEVERITY_COLORS: Record<string, string> = {
  minor: "bg-blue-100 text-blue-800",
  moderate: "bg-yellow-100 text-yellow-800",
  serious: "bg-orange-100 text-orange-800",
  critical: "bg-red-100 text-red-800",
};

const TYPE_LABELS: Record<string, string> = {
  injury: "Injury",
  near_miss: "Near Miss",
  property_damage: "Property Damage",
  environmental: "Environmental",
  vehicle: "Vehicle",
  other: "Other",
};

function IncidentDetailPage() {
  const { incidentId } = Route.useParams();
  const navigate = useNavigate();
  const goBack = useGoBack({ to: "/incidents" });
  const queryClient = useQueryClient();

  const [investigatorUserId, setInvestigatorUserId] = useState("");
  const [investigationNotes, setInvestigationNotes] = useState("");
  const [rootCause, setRootCause] = useState("");
  const [preventiveActions, setPreventiveActions] = useState("");

  const { data, isLoading } = useQuery({
    queryKey: ["incident", incidentId],
    queryFn: () =>
      apiGet<{ data: any }>(`/api/incidents/${incidentId}`),
  });

  const { data: usersData } = useQuery({
    queryKey: ["org-users"],
    queryFn: () => apiGet<{ data: Array<{ id: string; name: string }> }>("/api/users"),
  });

  const incident = data?.data;
  const orgUsers = usersData?.data || [];

  const investigateMutation = useMutation({
    mutationFn: () =>
      apiPost(`/api/incidents/${incidentId}/investigate`, {
        investigatorUserId,
        investigationNotes: investigationNotes || undefined,
      }),
    onSuccess: () => {
      toast.success("Investigation started");
      queryClient.invalidateQueries({ queryKey: ["incident", incidentId] });
      queryClient.invalidateQueries({ queryKey: ["incidents"] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const closeMutation = useMutation({
    mutationFn: () =>
      apiPost(`/api/incidents/${incidentId}/close`, {
        rootCause,
        preventiveActions,
      }),
    onSuccess: () => {
      toast.success("Incident closed");
      queryClient.invalidateQueries({ queryKey: ["incident", incidentId] });
      queryClient.invalidateQueries({ queryKey: ["incidents"] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  if (isLoading) {
    return (
      <div className="space-y-4 max-w-lg mx-auto pt-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  if (!incident) {
    return (
      <div className="text-center pt-12">
        <p className="text-muted-foreground">Incident not found</p>
      </div>
    );
  }

  return (
    <div className="space-y-4 max-w-lg mx-auto pb-20">
      <div className="flex items-center gap-2 pt-2">
        <Button
          variant="ghost"
          size="sm"
          className="p-1"
          onClick={goBack} aria-label="Back"
        >
          <ChevronLeft className="h-5 w-5" />
        </Button>
        <h1 className="text-xl font-bold text-text-primary">
          Incident Detail
        </h1>
      </div>

      <Card>
        <CardContent className="py-3 px-4 space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            <Badge
              className={cn(
                "text-xs",
                SEVERITY_COLORS[incident.severity]
              )}
            >
              {incident.severity}
            </Badge>
            <Badge variant="outline">
              {TYPE_LABELS[incident.incidentType] || incident.incidentType}
            </Badge>
            <Badge variant="secondary">
              {incident.status.replace("_", " ")}
            </Badge>
          </div>

          <p className="text-sm">{incident.description}</p>

          <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground">
            <div>
              <span className="font-medium">Date:</span>{" "}
              {formatDateOnly(incident.date)}
              {incident.time && ` at ${incident.time}`}
            </div>
            {incident.location && (
              <div>
                <span className="font-medium">Location:</span>{" "}
                {incident.location}
              </div>
            )}
          </div>

          {incident.immediateActions && (
            <div className="border-t pt-2">
              <p className="text-xs font-medium text-muted-foreground">
                Immediate Actions
              </p>
              <p className="text-sm">{incident.immediateActions}</p>
            </div>
          )}

          {incident.investigationNotes && (
            <div className="border-t pt-2">
              <p className="text-xs font-medium text-blue-700">
                Investigation Notes
              </p>
              <p className="text-sm">{incident.investigationNotes}</p>
            </div>
          )}

          {incident.rootCause && (
            <div className="border-t pt-2">
              <p className="text-xs font-medium text-green-700">Root Cause</p>
              <p className="text-sm">{incident.rootCause}</p>
            </div>
          )}

          {incident.preventiveActions && (
            <div>
              <p className="text-xs font-medium text-green-700">
                Preventive Actions
              </p>
              <p className="text-sm">{incident.preventiveActions}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Witnesses */}
      {(incident.witnesses || []).length > 0 && (
        <div className="space-y-2">
          <h3 className="text-sm font-semibold flex items-center gap-1">
            <Users className="h-4 w-4" /> Witnesses
          </h3>
          {incident.witnesses.map((w: any) => (
            <Card key={w.id}>
              <CardContent className="py-2 px-3">
                <p className="text-xs font-medium">{w.userName}</p>
                {w.statement && <p className="text-sm">{w.statement}</p>}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Investigate action */}
      {incident.status === "reported" && (
        <div className="space-y-3 border rounded-lg p-4">
          <h3 className="text-sm font-semibold flex items-center gap-1">
            <Search className="h-4 w-4" /> Start Investigation
          </h3>
          <div className="space-y-2">
            <Label>Investigator *</Label>
            <SearchableSelect
              value={investigatorUserId}
              onChange={setInvestigatorUserId}
              allLabel="Select investigator..."
              allValue=""
              searchPlaceholder="Type a name..."
              options={orgUsers.map((u) => ({ value: u.id, label: u.name }))}
            />
          </div>
          <div className="space-y-2">
            <Label>Notes</Label>
            <textarea
              value={investigationNotes}
              onChange={(e) => setInvestigationNotes(e.target.value)}
              placeholder="Initial investigation notes..."
              rows={2}
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            />
          </div>
          <Button
            className="w-full"
            disabled={!investigatorUserId || investigateMutation.isPending}
            onClick={() => investigateMutation.mutate()}
          >
            {investigateMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              "Begin Investigation"
            )}
          </Button>
        </div>
      )}

      {/* Close action */}
      {incident.status !== "closed" && (
        <div className="space-y-3 border rounded-lg p-4">
          <h3 className="text-sm font-semibold flex items-center gap-1">
            <XCircle className="h-4 w-4" /> Close Incident
          </h3>
          <div className="space-y-2">
            <Label>Root Cause *</Label>
            <textarea
              value={rootCause}
              onChange={(e) => setRootCause(e.target.value)}
              placeholder="What was the root cause?"
              rows={2}
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            />
          </div>
          <div className="space-y-2">
            <Label>Preventive Actions *</Label>
            <textarea
              value={preventiveActions}
              onChange={(e) => setPreventiveActions(e.target.value)}
              placeholder="What will be done to prevent recurrence?"
              rows={2}
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            />
          </div>
          <Button
            variant="outline"
            className="w-full text-green-700 border-green-300"
            disabled={
              !rootCause || !preventiveActions || closeMutation.isPending
            }
            onClick={() => closeMutation.mutate()}
          >
            {closeMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                <CheckCircle className="h-4 w-4 mr-1" /> Close Incident
              </>
            )}
          </Button>
        </div>
      )}
    </div>
  );
}
